import { User, UserManager } from 'oidc-client-ts'
import { createContext } from 'react'
import { USER_MANAGER } from './userManager'

export const InternalRoleId = '594cd817-6de7-4575-b8ee-f93860db0cf8'
export const SupplierRoleId = '911d0007-8805-4d2c-a338-c9aad3e337b2'
export const AdminRoleId = '66a86ef7-2a61-489b-b5c0-bfe617527385'
export const ManagerRoleId = '1625be50-89e9-4aab-97c1-0d59c0f082d2'
export const GuestRoleId = 'c5f44132-0b33-4372-bcfb-d00ff46ee695'
export const AccountManagerRole = '111111b1-957f-499e-aab5-7256e5e6d31e'
export const CustomerServiceRole = '58d60258-8512-421e-aa8b-11c570ebff65'
export const TechnicRole = '77f64612-6cd5-491a-a83b-b695a14bb54c'
export const InsuranceRole = '377db285-9838-43e3-aa3c-fa618b226fee'
export const FinanceRole = '5edefba7-e5f2-48b2-96ac-b2e82109d55d'
export const RemarketingRole = '51d52ab5-6d43-4ed5-97f6-01640f0311fc'
export const InterfleetProTags = ['4upro', 'interfleetpro']

export interface UserContext {
  id: string
  email?: string
  permissions: string[]
  displayName?: string
  roles: string[]
  locale?: string
  isAdmin: boolean
  isInternalUser: boolean
  isAccountManager: boolean
  isCustomerService: boolean
  isTechnic: boolean
  isInsurance: boolean
  isFinance: boolean
  isRemarketing: boolean
  is4UPro: boolean
  isManager: boolean
  isSupplier: boolean
  isGuest: boolean
  canManageOrders: boolean
  isImpersonated: boolean
  tenant?: string
  tags: string[]
}

export interface UserAuthContext {
  manager: UserManager
  user: UserContext | null | undefined
  primaryUser: UserContext | null | undefined
  impersonate: (tenantId: string | undefined) => Promise<void>
  signout: () => void
}

export const AuthContext = createContext<UserAuthContext>({
  manager: USER_MANAGER,
  user: undefined,
  primaryUser: undefined,
  impersonate: async () => undefined,
  signout: () => undefined,
})

export function decodeUserProfile(token: string) {
  try {
    return JSON.parse(atob(token.split('.')[1])) as Record<string, unknown>
  } catch {
    return {}
  }
}

export function getUserContext(user: User): UserContext {
  const claims = decodeUserProfile(user.access_token)
  const role = user.profile['role'] ?? ([] as string | string[])
  const permission = user.profile['permission'] ?? ([] as string | string[])
  const tag = user.profile['tag'] ?? ([] as string | string[])
  const tenant = claims.tenant as string | undefined
  const locale = user.profile.locale?.split('-').at(0)
  const roles = Array.isArray(role) ? role : [role]
  const tags: string[] = Array.isArray(tag) ? tag : [tag]
  const permissions = Array.isArray(permission) ? permission : [permission]

  const canManageOrders =
    roles.includes(InternalRoleId) || roles.includes(AdminRoleId) || permissions.includes('orders.manage')

  const amr: string[] = Array.isArray(user.profile.amr) ? user.profile.amr : user.profile.amr ? [user.profile.amr] : []
  return {
    id: user.profile.sub,
    email: user.profile.email,
    permissions: Array.isArray(permission) ? permission : [permission],
    displayName: user.profile.name ?? user.profile.preferred_username,
    roles,
    locale,
    isAdmin: roles.includes(AdminRoleId),
    isInternalUser: roles.includes(InternalRoleId) || roles.includes(AdminRoleId),
    isAccountManager: roles.includes(AccountManagerRole),
    isCustomerService: roles.includes(CustomerServiceRole),
    isTechnic: roles.includes(TechnicRole),
    isInsurance: roles.includes(InsuranceRole),
    isFinance: roles.includes(FinanceRole),
    isRemarketing: roles.includes(RemarketingRole),
    is4UPro: tags.some((t) => InterfleetProTags.includes(t.toLowerCase())),
    isManager: roles.includes(ManagerRoleId),
    isSupplier: roles.includes(SupplierRoleId),
    isGuest: roles.includes(GuestRoleId),
    canManageOrders,
    isImpersonated: amr.some((a) => a.toLowerCase().includes('token-exchange')),
    tenant,
    tags,
  }
}
